/**
 * Learning System API Service
 *
 * Provides functions for accessing learning reports and insights:
 * - Get learning analysis report
 * - Process unanalyzed trades manually
 * - Access pattern analysis data
 *
 * Phase 5 Implementation
 */

import { apiClient } from "./apiClient";
import { logger } from "./logger";

export interface LearningPattern {
  pattern_type: string;
  pattern_value: string;
  occurrence_count: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
  avg_profit: number;
  avg_loss: number;
  profit_factor: number;
  confidence: number;
}

export interface IndicatorStat {
  win_rate: number;
  total_trades: number;
  net_pnl: number;
}

export interface LearningReport {
  generated_at: string;
  analysis_period_days: number;
  total_trades_analyzed: number;
  successful_patterns: LearningPattern[];
  failed_patterns: LearningPattern[];
  insights: string[];
  recommendations: string[];
  rsi_ranges: Record<string, IndicatorStat>;
  macd_patterns: Record<string, IndicatorStat>;
  ema_patterns: Record<string, IndicatorStat>;
  ta_confidence_patterns: Record<string, IndicatorStat>;
  ta_signal_strength_patterns: Record<string, IndicatorStat>;
  ta_overall_score_ranges: Record<string, IndicatorStat>;
  ta_mtf_analysis: Record<string, Record<string, IndicatorStat>>;
  ta_breakout_analysis: Record<string, IndicatorStat>;
  ta_volume_patterns: Record<string, IndicatorStat>;
  ta_volatility_patterns: Record<string, IndicatorStat>;
}

export interface ProcessUnanalyzedResponse {
  processed_count: number;
  status: string;
  message: string;
}

export async function getLearningReport(days: number = 90): Promise<LearningReport> {
  try {
    const response = await apiClient.get<LearningReport>(
      `/admin/reports/learning?days=${days}`,
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error fetching learning report:", error as Error);
    throw error;
  }
}

export async function processUnanalyzedTrades(days: number = 90): Promise<ProcessUnanalyzedResponse> {
  try {
    const response = await apiClient.post<ProcessUnanalyzedResponse>(
      `/admin/reports/learning/process`,
      { days },
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error processing unanalyzed trades:", error as Error);
    throw error;
  }
}
